import { ChevronRight } from '@mui/icons-material'
import { Alert, Box, Stack } from '@mui/material'
import isNil from 'lodash/isNil'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { assertNever } from 'utils/type-guard/assertNever'
import { Header } from '@omnea-app/fe-app-shell/headers/Header'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useIsMobileWidth } from '@omnea-app/fe-browser-utils/useDetectDevice'
import { DashboardBanner as FormInsightsBanner } from '@omnea-app/fe-form-builder/DashboardBanner'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { generateRequestsListRoute } from '@omnea-app/fe-requests/requestRoutes'
import { useRequestStats } from '@omnea-app/fe-requests/useRequestStats'
import { RequestRow } from '@omnea-app/fe-requests/useRequestTableRows'
import { InternalLinkButton } from '@omnea-app/fe-routing/InternalLinkButton'
import { TaskRow, useTaskTableRowsForCurrentUser } from '@omnea-app/fe-tasks/useTaskTableRows'
import { useGetUserByEmailSuspense } from '@omnea-app/fe-users-and-groups/useGetUserByEmail'
import { CelebrationBanner } from '@omnea-app/frontend/components/shared/banners/CelebrationBanner'
import { useCelebrationBanner } from '@omnea-app/frontend/components/shared/banners/useCelebrationBanner'
import { StatsContext } from '@omnea-app/frontend/providers/StatsProvider'
import { useUnpublishedWorkflowsBannerInfo } from '@omnea-app/frontend/utils/hooks/useUnpublishedWorkflowsBannerInfo'
import { DashboardSummary, Levels } from './components/dashboard-summary'
import { OutOfOfficeBanner } from './components/dashboard-summary/OutOfOfficeBanner'
import { DashboardTable } from './components/dashboard-table'
import { DashboardEmptyState } from './components/dashboard-table/DashboardEmptyState'
import { MentionsList } from './components/mentions/MentionsList'
import { MOBILE_MENTIONS_SECTION_ID, ScrollToMentionsBanner } from './components/mentions/ScrollToMentionsBanner'
import { RfxTable } from './components/rfx-table/RfxTable'
import { useLatestRequests } from './hooks/requests/useLatestRequests'
import { useMyCompleteRequests } from './hooks/requests/useMyCompleteRequests'
import { useMyPendingRequests } from './hooks/requests/useMyPendingRequests'
import { useRenewalCount } from './hooks/useRenewalCount'
import type { SortModel } from './components/dashboard-table/sortRows'
import type { DashboardTableType } from './components/dashboard-table/types'

interface Section {
  type: DashboardTableType
  title: string
  rows: TaskRow[] | RequestRow[]
  visibleColumns: string[]
  defaultSortModel?: SortModel
  showNewRequestButtonIfEmpty?: boolean
}

export const Dashboard = () => {
  const { user } = useAuthenticatedUser()
  const { data: userData } = useGetUserByEmailSuspense(user.email)
  const { t } = useTranslation('dashboard')
  const { RFX } = useFeatureFlags()
  const { numRenewalsActionNeeded } = useContext(StatsContext)

  const isMobileWidth = useIsMobileWidth()

  const { data: requestStats, isLoading: requestStatsLoading } = useRequestStats()
  const { data: myPendingRequests, isLoading: myPendingRequestsLoading } = useMyPendingRequests()
  const { data: myCompleteRequests, isLoading: myCompleteRequestsLoading } = useMyCompleteRequests()
  const { data: latestRequests, isLoading: latestRequestsLoading } = useLatestRequests()
  const { rows: pendingApprovalRows, loading: approvalLoading } = useTaskTableRowsForCurrentUser('pending')
  const { shouldShowBanner, bannerText } = useUnpublishedWorkflowsBannerInfo()

  const pendingRequestCount = requestStats?.pending ?? 0
  const delayedRequestCount = requestStats?.delayed ?? 0
  const completedRequestCount = requestStats?.completed ?? 0
  const inProgressRenewalCount = useRenewalCount()

  const isAdmin = user.isAdmin
  const isApprover = pendingApprovalRows.length > 0

  const celebrationBanner = useCelebrationBanner({
    completedRequestCount,
  })

  const summaryItemsAdmin = [
    {
      title: t('summary-items.active'),
      count: pendingRequestCount,
      level: Levels.NORMAL,
      url: generateRequestsListRoute('pending'),
    },
    {
      title: t('summary-items.delayed'),
      count: delayedRequestCount,
      level: delayedRequestCount > 0 ? Levels.ALERT : Levels.NORMAL,
      isWarningEnabled: delayedRequestCount > 0,
      url: generateRequestsListRoute('delayed'),
    },
    {
      title: t('summary-items.complete'),
      count: completedRequestCount,
      level: Levels.NORMAL,
      url: generateRequestsListRoute('completed'),
    },
    {
      title: t('summary-items.active-tasks'),
      count: pendingApprovalRows?.length,
      level: Levels.NORMAL,
      url: '/tasks/pending',
    },
    {
      title: t('summary-items.action-needed-renewals'),
      count: numRenewalsActionNeeded ?? 0,
      level: !isNil(numRenewalsActionNeeded) && numRenewalsActionNeeded > 0 ? Levels.WARNING : Levels.NORMAL,
      url: '/renewals/actionNeeded',
      hiddenOnMobile: true,
    },
    {
      title: t('summary-items.in-progress-renewals'),
      count: inProgressRenewalCount,
      level: Levels.NORMAL,
      url: '/renewals/inProgress',
      hiddenOnMobile: true,
    },
  ]

  const summaryItemsApprover = [
    {
      title: t('summary-items.active-tasks'),
      count: pendingApprovalRows?.length,
      level: Levels.NORMAL,
      url: '/tasks/pending',
    },
    {
      title: t('summary-items.active'),
      count: pendingRequestCount,
      level: Levels.NORMAL,
      url: generateRequestsListRoute('minePending'),
    },
    {
      title: t('summary-items.complete'),
      count: completedRequestCount,
      level: Levels.NORMAL,
      url: generateRequestsListRoute('mineCompleted'),
    },
  ]

  const summaryItemsNonApprover = [
    {
      title: t('summary-items.active'),
      count: pendingRequestCount,
      level: Levels.NORMAL,
      url: generateRequestsListRoute('minePending'),
    },
    {
      title: t('summary-items.complete'),
      count: completedRequestCount,
      level: Levels.NORMAL,
      url: generateRequestsListRoute('mineCompleted'),
    },
  ]

  const nonAdminSummaryItems = isApprover ? summaryItemsApprover : summaryItemsNonApprover
  const summaryItems = isAdmin ? summaryItemsAdmin : nonAdminSummaryItems

  const sectionsAdmin: Section[] = [
    {
      type: 'TASKS',
      title: t('request-section.title.your-tasks'),
      rows: pendingApprovalRows,
      visibleColumns: ['name', 'label', 'supplierName', 'dueOn'],
      defaultSortModel: { field: 'dueDate', sort: 'asc' },
    },
    {
      type: 'REQUESTS',
      title: t('request-section.title.latest-requests'),
      rows: latestRequests ?? [],
      visibleColumns: ['name', 'suppliers', 'stage', 'createdBy', 'createdAt'],
    },
  ]

  const userCompletedRequests = myCompleteRequests ?? []
  const userPendingRequests = myPendingRequests ?? []

  const sectionsApprover: Section[] = [
    {
      type: 'TASKS',
      title: t('request-section.title.your-tasks'),
      rows: pendingApprovalRows,
      visibleColumns: ['name', 'label', 'supplierName', 'dueOn'],
    },
    {
      type: 'REQUESTS',
      title: t('request-section.title.active-requests'),
      rows: userPendingRequests,
      showNewRequestButtonIfEmpty: true,
      visibleColumns: ['name', 'suppliers', 'stage', 'assignees'],
    },
    {
      type: 'REQUESTS',
      title: t('request-section.title.complete'),
      rows: userCompletedRequests,
      visibleColumns: ['name', 'suppliers', 'stage'],
    },
  ]

  const sectionsNonApprover: Section[] = [
    {
      type: 'REQUESTS',
      title: t('request-section.title.active-requests'),
      rows: userPendingRequests,
      showNewRequestButtonIfEmpty: true,
      visibleColumns: ['name', 'suppliers', 'stage', 'assignees'],
    },
    {
      type: 'REQUESTS',
      title: t('request-section.title.complete'),
      rows: userCompletedRequests,
      visibleColumns: ['name', 'suppliers', 'stage'],
    },
  ]

  const nonAdminSections = isApprover ? sectionsApprover : sectionsNonApprover
  const sections = isAdmin ? sectionsAdmin : nonAdminSections
  const requestSections = sections.filter((section) => section.type === 'REQUESTS')
  const taskSections = sections.filter((section) => section.type === 'TASKS')

  const isNonAdminAndRequestSectionsAreEmpty =
    !myPendingRequestsLoading &&
    !myCompleteRequestsLoading &&
    !latestRequestsLoading &&
    !isAdmin &&
    requestSections.map((requestSection) => requestSection.rows).every((requestSectionRows) => requestSectionRows.length === 0)

  const isLoading = (type: DashboardTableType) => {
    switch (type) {
      case 'REQUESTS':
        return myPendingRequestsLoading || myCompleteRequestsLoading || latestRequestsLoading

      case 'TASKS':
        return approvalLoading
      default:
        assertNever(type)
        return false
    }
  }

  return (
    <Stack height="100%">
      <Header title={t('title')} />
      <Stack direction="row" flex={1}>
        <Stack
          sx={{
            flex: 1,
            py: { xs: 4, md: 7 },
            px: { xs: 4, md: 11 },
            width: '100%',
            maxWidth: { md: 'calc(100% - 320px)' },
            borderRight: { md: '1px solid' },
            borderColor: { md: 'neutralLight.300' },
          }}
          gap={{ xs: 6, md: 10 }}
        >
          <Stack gap={1}>
            {celebrationBanner.shouldShowBanner && (
              <CelebrationBanner
                title={celebrationBanner.title}
                subtitle={celebrationBanner.subtitle}
                onClose={celebrationBanner.onDismiss}
              />
            )}
            {shouldShowBanner && (
              <Stack mb={4}>
                <Alert
                  severity="error"
                  action={
                    <InternalLinkButton href="/settings/workflows">
                      <ChevronRight />
                    </InternalLinkButton>
                  }
                >
                  {bannerText}
                </Alert>
              </Stack>
            )}
            {!user.isSupplier && userData?.isOutOfOffice && <OutOfOfficeBanner userId={user.id} />}
            {isAdmin && !isMobileWidth ? <FormInsightsBanner /> : null}
            {isMobileWidth && <ScrollToMentionsBanner />}
            <DashboardSummary summaryItems={summaryItems} loading={approvalLoading || requestStatsLoading} />
          </Stack>
          {RFX && <RfxTable />}
          {taskSections.map((section, i) => (
            <DashboardTable key={i} loading={isLoading(section.type)} {...section} />
          ))}
          {isNonAdminAndRequestSectionsAreEmpty ? (
            <DashboardEmptyState />
          ) : (
            requestSections.map((section, i) => <DashboardTable key={i} loading={isLoading(section.type)} {...section} />)
          )}
          {isMobileWidth ? (
            <Box id={MOBILE_MENTIONS_SECTION_ID}>
              <MentionsList isMobile={isMobileWidth} />
            </Box>
          ) : null}
        </Stack>
        {isMobileWidth ? null : <MentionsList isMobile={isMobileWidth} />}
      </Stack>
    </Stack>
  )
}
